import type { CalendarEvent } from 'src/meetings/models';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useCalendarEventMeetingAgenda } from './useCalendarEventMeetingAgenda';
import { useCalendarEventMeetingNote } from './useCalendarEventMeetingNote';
import { useGenerateCalendarEventMeetingNote } from './useGenerateCalendarEventMeetingNote';

// Regenerates the meeting's note from whatever it was first built from: its template, or the
// agenda file or generated agenda it followed.
export const useRegenerateMeetingNote = (event: CalendarEvent) => {
  const { meetingNote, refetch } = useCalendarEventMeetingNote(event.id, {
    enabled: event.hasTranscription,
  });
  const { generateMeetingNote, isLoading: isRegenerating } = useGenerateCalendarEventMeetingNote(
    event.id,
  );
  const { meetingAgenda } = useCalendarEventMeetingAgenda({
    eventId: event.id,
    enabled: event.hasMeetingAgenda,
  });

  const templateId = meetingNote?.templateId;
  const agendaFileId = meetingNote?.agendaFileId;
  const meetingAgendaId = meetingNote?.meetingAgendaId ?? meetingAgenda?.id;
  const canRegenerate = templateId != null || agendaFileId != null || meetingAgendaId != null;

  const regenerate = async () => {
    try {
      if (templateId != null) {
        await generateMeetingNote({ templateId, summarise: true });
      } else if (agendaFileId != null) {
        await generateMeetingNote({ agendaFileId, summarise: true });
      } else if (meetingAgendaId != null) {
        await generateMeetingNote({ meetingAgendaId, summarise: true });
      } else {
        return;
      }
      refetch();
    } catch (error) {
      notifyAndLogError('Failed to regenerate meeting note', 'Please try again later', error, {
        eventId: event.id,
        templateId,
        agendaFileId,
        meetingAgendaId,
      });
    }
  };

  return { meetingNote, refetch, canRegenerate, regenerate, isRegenerating };
};
